// RouteLink.js
import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";
import UserManagement from "./Pages/UserManagement/UserManagement";

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import("./Pages/Login/LoginPage"));
const DashboardLayout = React.lazy(() => import("./Layout/DashboardLayout"));
const DashboardHome = React.lazy(
  () => import("./Pages/Dashboard/DashboardHome"),
);
const PrivateRoute = React.lazy(() => import("./Utilities/PrivateRoute"));
const Courses = React.lazy(() => import("./Pages/University/Courses/Courses"));
const Department = React.lazy(
  () => import("./Pages/University/Department/Department"),
);
const Majors = React.lazy(() => import("./Pages/University/Majors/Majors"));
const StudentProfile = React.lazy(
  () => import("./Pages/UserManagement/StudentProfile/StudentProfile"),
);
const CreateUniAdmin = React.lazy(
  () => import("./Pages/UserManagement/CreateUniAdmin/CreateUniAdmin"),
);
const UniAdminList = React.lazy(
  () => import("./Pages/UserManagement/CreateUniAdmin/UniAdminList"),
);
const UserList = React.lazy(
  () => import("./Pages/UserManagement/UserList/UserList"),
);
const SuperUserList = React.lazy(
  () => import("./Pages/SuperUserManagement/SuperUserList/SuperUserList"),
);
const AddSuperUserList = React.lazy(
  () => import("./Pages/SuperUserManagement/SuperUserList/AddSuperUserList"),
);
const UserPermission = React.lazy(
  () => import("./Pages/SuperUserManagement/SuperUserPermission/SuperUserPermission"),
);
const AddUserPermission = React.lazy(
  () => import("./Pages/SuperUserManagement/SuperUserPermission/AddSuperUserPermission"),
);
const Universities = React.lazy(
  () => import("./Pages/University/Universities/Universities"),
);
const AddUniversities = React.lazy(
  () => import("./Pages/University/Universities/AddUniversities"),
);
const OnboardingRequests = React.lazy(
  () => import("./Pages/OnboardingRequests/OnboardingRequests"),
);
const SubscriptionManagement = React.lazy(
  () => import("./Pages/SubscriptionManagement/SubscriptionManagement"),
);
const OnboardingExperience = React.lazy(
  () => import("./Pages/OnboardingExperience/OnboardingExperience"),
);
const WellnessManagement = React.lazy(
  () => import("./Pages/WellnessManagement/WellnessManagement"),
);
const Explore = React.lazy(() => import("./Pages/Explore/Explore"));
const DealsManagement = React.lazy(
  () => import("./Pages/DealsManagement/DealsManagement"),
);
const AdvertisementManagement = React.lazy(
  () => import("./Pages/AdvertisementManagement/AdvertisementManagement"),
);
const EventsManagement = React.lazy(
  () => import("./Pages/EventsManagement/EventsManagement"),
);
const StartupEcosystem = React.lazy(
  () => import("./Pages/StartupEcosystem/StartupEcosystem"),
);
const InteractionManagement = React.lazy(
  () => import("./Pages/InteractionManagement/InteractionManagement"),
);
const ContentModeration = React.lazy(
  () => import("./Pages/ContentModeration/ContentModeration"),
);
const AnalyticsReports = React.lazy(
  () => import("./Pages/AnalyticsReports/AnalyticsReports"),
);
const Notifications = React.lazy(
  () => import("./Pages/Notifications/Notifications"),
);
const PlatformSettings = React.lazy(
  () => import("./Pages/PlatformSettings/PlatformSettings"),
);
const AuditLogs = React.lazy(() => import("./Pages/AuditLogs/AuditLogs"));
const CommunityCategory = React.lazy(
  () => import("./Pages/PostManagement/CommunityCategory/CommunityCategory"),
);
const Community = React.lazy(
  () => import("./Pages/PostManagement/Community/Community"),
);
const AddCommunity = React.lazy(
  () => import("./Pages/PostManagement/Community/AddCommunity"),
);
const NewPost = React.lazy(
  () => import("./Pages/PostManagement/NewPost/NewPost"),
);
const PostList = React.lazy(
  () => import("./Pages/PostManagement/PostList/PostList"),
);

const routes = [
  {
    path: "/login",
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PrivateRoute />
      </Suspense>
    ),
    children: [
      {
        path: "/dashboard",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <DashboardLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <DashboardHome />
              </Suspense>
            ),
          },
          {
            path: "university/courses",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Courses />
              </Suspense>
            ),
          },
          {
            path: "university/department",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Department />
              </Suspense>
            ),
          },
          {
            path: "university/majors",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Majors />
              </Suspense>
            ),
          },
          {
            path: "university/universities",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Universities />
              </Suspense>
            ),
          },
          {
            path: "university/universities/add",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddUniversities />
              </Suspense>
            ),
          },
          {
            path: "university/universities/edit/:id",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddUniversities />
              </Suspense>
            ),
          },
          {
            path: "university/user-list",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserList />
              </Suspense>
            ),
          },
          {
            path: "university/userlist",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserList />
              </Suspense>
            ),
          },

          {
            path: "superuser-management/superuserlist",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <SuperUserList />
              </Suspense>
            ),
          },
          {
            path: "superuser-management/superuserlist/add",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddSuperUserList />
              </Suspense>
            ),
          },
          {
            path: "superuser-management/superuserlist/edit/:id",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddSuperUserList />
              </Suspense>
            ),
          },
          {
            path: "university/userpermission",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserPermission />
              </Suspense>
            ),
          },
          {
            path: "university/userpermission/manage/:adminId",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddUserPermission />
              </Suspense>
            ),
          },
          {
            path: "user-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserManagement />
              </Suspense>
            ),
          },
          {
            path: "user-management/student-profile",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <StudentProfile />
              </Suspense>
            ),
          },
          {
            path: "user-management/uniadminlist",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UniAdminList />
              </Suspense>
            ),
          },
          {
            path: "user-management/create-uniadmin",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <CreateUniAdmin />
              </Suspense>
            ),
          },
          {
            path: "user-management/userlist",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserList />
              </Suspense>
            ),
          },
          {
            path: "user-management/user-list",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserList />
              </Suspense>
            ),
          },
          {
            path: "onboarding-requests",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <OnboardingRequests />
              </Suspense>
            ),
          },
          {
            path: "subscription-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <SubscriptionManagement />
              </Suspense>
            ),
          },
          {
            path: "onboarding-experience",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <OnboardingExperience />
              </Suspense>
            ),
          },
          {
            path: "wellness-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WellnessManagement />
              </Suspense>
            ),
          },
          {
            path: "explore",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Explore />
              </Suspense>
            ),
          },
          {
            path: "deals-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <DealsManagement />
              </Suspense>
            ),
          },
          {
            path: "advertisement-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AdvertisementManagement />
              </Suspense>
            ),
          },
          {
            path: "events-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <EventsManagement />
              </Suspense>
            ),
          },
          {
            path: "startup-ecosystem",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <StartupEcosystem />
              </Suspense>
            ),
          },
          {
            path: "interaction-management",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <InteractionManagement />
              </Suspense>
            ),
          },
          {
            path: "content-moderation",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ContentModeration />
              </Suspense>
            ),
          },
          {
            path: "analytics-reports",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AnalyticsReports />
              </Suspense>
            ),
          },
          {
            path: "notifications",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Notifications />
              </Suspense>
            ),
          },
          {
            path: "platform-settings",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PlatformSettings />
              </Suspense>
            ),
          },
          {
            path: "audit-logs",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AuditLogs />
              </Suspense>
            ),
          },
          {
            path: "communitycategory",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <CommunityCategory />
              </Suspense>
            ),
          },
          {
            path: "community",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Community />
              </Suspense>
            ),
          },
          {
            path: "community/add",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddCommunity />
              </Suspense>
            ),
          },
          {
            path: "community/edit/:id",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddCommunity />
              </Suspense>
            ),
          },
          {
            path: "post-management/new-post",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <NewPost />
              </Suspense>
            ),
          },
          {
            path: "post-management/post-list",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PostList />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;
